import React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import { objectAttribute } from 'utils/resources/ObjectAttributes';
import {
  Section,
  ClinicPersonnelTableErrorMessages,
  SolePractitionerToggle,
} from 'components/shared';
import { ResellerForm } from './ResellerForm';
import { PersonnelTable } from './PersonnelTable';
import { AddPersonModal } from './AddPersonModal';
import FeatureFlagsContext from '../FeatureFlagsContext';

export default function ClinicPersonnel(props) {
  const featureFlags = React.useContext(FeatureFlagsContext);
  const [allRoleOptions, setAllRoleOptions] = React.useState([]);

  React.useEffect(() => {
    objectAttribute({
      objectType: 'vet',
      attribute: 'relationship_types',
    }).then(setAllRoleOptions);
  }, []);

  const formik = useFormik({ initialValues: props.initialFormState });
  const [validationErrors, setValidationErrors] = React.useState(
    props.initialValidationErrors || {}
  );

  const blankPerson = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    practiceRoles: [],
    vetLicenseNumber: '',
    areaOfInterest: [],
    ownerPercentage: '',
    boardCertifications: [],
    vetSchool: '',
    vetDegree: '',
    mainContact: false,
  };
  const defaultModalState = {
    open: false,
    mode: null,
    currentPerson: blankPerson,
  };
  const [modalState, setModalState] = React.useState(defaultModalState);
  const resetModalState = () => setModalState(defaultModalState);

  const hasInvalidValue = (attribute) =>
    validationErrors.hasOwnProperty(attribute);
  const showErrors = (attribute) =>
    !!formik.touched[attribute] && hasInvalidValue(attribute);
  const errorText = (attribute) =>
    showErrors(attribute) ? validationErrors[attribute].join(' ') : '';
  const [loading, setLoading] = React.useState(false);

  const saveForm = (attributes) => {
    return props.onSave(attributes).then(({ validationErrors }) => {
      setValidationErrors(validationErrors);
      return Promise.resolve({ validationErrors });
    });
  };

  // Use { leading: true } option in order to return a promise from the debounced function
  const persistFormState = React.useMemo(
    () => debounce(saveForm, 300, { leading: true }),
    []
  );

  const handleChange = (e) => {
    formik.handleChange(e);

    const value =
      e.target.type === 'radio' ? e.target.value === 'true' : e.target.value;

    persistFormState({ ...formik.values, [e.target.name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Clear editingPerson before submission in the event it has validation errors and the user resumes a form w/o closing it in its original tab
    // The user may not want to save the person in the hanging form, if the rest of the form is valid they should be able to submit

    // The editingPerson data is saved in the backend as its part of the form and it also has validation criteria that must be met. If the
    // editingPerson has validationErrors then that person won't get created or updated (Add vs Edit). Creating or updating implies adding
    // the person to the people array or updating the person in the people array, respectively. Therefore, the people array can only contain
    // personnel that have passed validation. This allows us to safely clear the editingPerson data before submission b/c none of the people
    // being saved have the validation errors that the editingPerson has.
    const persistentFormValues = {
      ...formik.values,
      editingPerson: null,
      editingIndex: null,
    };

    return saveForm(persistentFormValues).then(
      ({ validationErrors: latestValidationErrors }) => {
        const touched = Object.keys(persistentFormValues).reduce(
          (touched, key) => {
            touched[key] = true;
            return touched;
          },
          {}
        );

        formik.setTouched(touched).then(() => {
          if (Object.keys(latestValidationErrors).length === 0) {
            props.onFinalFormSubmission();
          }
        });
      }
    );
  };

  const savePersonUpdates = (personAttributes) => {
    // Save AddOrEditPersonForm's form state
    return new Promise((resolve, reject) => {
      return persistFormState({
        ...formik.values,
        editingPerson: personAttributes,
      }).then(({ validationErrors }) => {
        resolve({ validationErrors: validationErrors.editingPerson || {} });
      });
    });
  };

  const people = (formik.values && formik.values.people) || [];

  const deletePerson = ({ index }) => {
    const updatedPeople = people.filter((_person, i) => i !== index);
    formik.setFieldValue('people', updatedPeople);
    return persistFormState({ ...formik.values, people: updatedPeople });
  };

  const openModal = ({ mode = null, editingIndex = null }) => {
    const nextPerson = mode === 'edit' ? people[editingIndex] : blankPerson;

    setModalState({ open: true, mode, currentPerson: nextPerson });

    formik.setFieldValue('editingIndex', editingIndex);

    persistFormState({
      ...formik.values,
      editingIndex,
      editingPerson: nextPerson,
    });
  };

  const handlePersonAdd = (attributes) => {
    const updatedPeople = [...people, attributes];

    formik.setFieldValue('people', updatedPeople);

    return persistFormState({
      ...formik.values,
      editingPerson: {},
      people: updatedPeople,
    }).then(() => {
      resetModalState();
    });
  };

  const handlePersonUpdate = (attributes) => {
    const updatedPeople = people.map((person, index) => {
      return index === formik.values.editingIndex ? attributes : person;
    });

    formik.setFieldValue('people', updatedPeople);

    return persistFormState({
      ...formik.values,
      editingIndex: null,
      editingPerson: {},
      people: updatedPeople,
    }).then(() => {
      resetModalState();
    });
  };

  const handleModalClose = () => {
    return persistFormState({
      ...formik.values,
      editingIndex: null,
      editingPerson: {},
    }).then(() => {
      resetModalState();
    });
  };

  const vets = people.filter((person) =>
    person.practiceRoles.includes('veterinarian')
  );

  React.useEffect(() => {
    if (vets.length > 1) {
      formik.setFieldValue('isSolePractitioner', false).then(() => {
        persistFormState({ ...formik.values, isSolePractitioner: false });
      });
    }
  }, [vets.length]);

  let componentList = [];

  componentList.push(
    <SolePractitionerToggle
      disabled={vets.length > 1}
      value={formik.values.isSolePractitioner}
      formLabel="Sole Practitioner (or Creating a Personal Account)?"
      onChange={handleChange}
    />
  );

  componentList.push(
    <PersonnelTable
      sectionComponentSize="large"
      people={people}
      deletePerson={deletePerson}
      openModal={({ mode, editingIndex }) => openModal({ mode, editingIndex })}
      allRoleOptions={allRoleOptions}
    />,
    <ClinicPersonnelTableErrorMessages
      sectionComponentSize="large"
      persistentErrorMessages={props.persistentErrorMessages}
      validationErrors={validationErrors}
      showErrors={true}
    />
  );

  const onlyHasEditingPersonValidationErrors =
    validationErrors.hasOwnProperty('editingPerson') &&
    Object.keys(validationErrors).length === 1;

  // Different front-end-clients/browser-tabs can make updates to the same form at the same time and we our form validation errors
  // are determined in the back-end/Hub. This creates a dilemma when the editingPerson modal is open and left in an error state and the user
  // tries finishing the form in a different browser without updating the editingPerson. For now, a straightforward solution for this
  // is to allow the user to progress if the modal is closed despite the fact that the form's editingPerson data is in an error state, as
  // long as the editingPerson validation errors are the only errors present. Therefore the submit button should be enabled in that
  // circumstance
  const allowProgress =
    Object.keys(validationErrors).length === 0 ||
    (onlyHasEditingPersonValidationErrors && !modalState.open);

  return (
    <ResellerForm
      tab="clinicPersonnel"
      location={props.location}
      allowProgress={allowProgress}
      loading={loading}
    >
      <form noValidate onSubmit={handleSubmit}>
        <Stack spacing={5.5}>
          <Section title="Clinic Personnel" components={componentList} />
          <AddPersonModal
            onSave={savePersonUpdates}
            addPerson={handlePersonAdd}
            updatePerson={handlePersonUpdate}
            open={modalState.open}
            onClose={handleModalClose}
            onCancel={handleModalClose}
            currentPerson={modalState.currentPerson}
            action={modalState.mode}
            noPeopleAdded={people.length === 0}
            isSolePractitioner={formik.values.isSolePractitioner}
            validationErrors={validationErrors.editingPerson || {}}
          />
          <Section
            components={[
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={props.goToPreviousPage}
              >
                BACK
              </Button>,
              <Button
                disabled={!allowProgress}
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                NEXT
              </Button>,
            ]}
          />
        </Stack>
      </form>
    </ResellerForm>
  );
}

ClinicPersonnel.propTypes = {
  validationErrors: PropTypes.object,
  clinicPersonnel: PropTypes.object,
};

ClinicPersonnel.defaultProps = {
  accountType: {},
  validationErrors: {
    clinicPersonnel: {},
  },
  clinicPersonnel: {
    people: [],
    isSolePractitioner: false,
  },
};
